import { createFileRoute, stripSearchParams, useNavigate } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { ProductCard } from "@/components/ProductCard";
import { CertificationsStrip } from "@/components/CertificationsStrip";
import { ReviewsMarquee } from "@/components/ReviewsMarquee";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/Eyebrow";
import { SectionTitle } from "@/components/SectionTitle";
import { CATEGORY_LABELS, ANIMAL_LABELS, effectivePrice, type Animal, type Category } from "@/data/products";
import { listPublicProducts } from "@/lib/public.functions";
import { useProductsHidden } from "@/hooks/useSiteSettings";
import { AnimalIcon, CategoryIcon } from "@/components/icons/BrandIcons";
import { Search, X, SlidersHorizontal, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { assertSectionVisible } from "@/lib/visibility";

// L'Abatina viene esclusa dai filtri (ha la sua pagina dedicata)
const CATEGORIES: Category[] = ["selezione-macellaio", "piatti-pronti", "preparati", "insaccati", "preconfezionati"];
const ANIMALS: Animal[] = ["manzo", "vitello", "maiale", "pollo", "tacchino", "agnello", "selvaggina"];
const SORTS = ["popolari", "prezzo-asc", "prezzo-desc", "nome", "novita"] as const;

const searchSchema = z.object({
  cat: fallback(z.array(z.string()), []).default([]),
  an: fallback(z.array(z.string()), []).default([]),
  q: fallback(z.string(), "").default(""),
  sort: fallback(z.enum(SORTS), "popolari").default("popolari"),
});

export const Route = createFileRoute("/prodotti/")({
  beforeLoad: () => assertSectionVisible("hide_products"),
  validateSearch: zodValidator(searchSchema),
  // I valori predefiniti dei filtri restano fuori dall'URL: /prodotti non deve redirigere a /prodotti?cat=…
  search: { middlewares: [stripSearchParams({ cat: [], an: [], q: "", sort: "popolari" as const })] },
  head: () => ({
    meta: [
      { title: "Prodotti — La Bottega delle Carni da Marco" },
      { name: "description", content: "Carni frollate, salumi, preparati e piatti pronti. Tutto preparato in bottega con cura artigianale a Campobasso." },
      { property: "og:title", content: "Catalogo prodotti — La Bottega delle Carni da Marco" },
      { property: "og:description", content: "Esplora il catalogo completo di carni e salumi della bottega." },
      { property: "og:url", content: "https://labottegadellecarnidamarco.it/prodotti" },
    ],
    links: [{ rel: "canonical", href: "https://labottegadellecarnidamarco.it/prodotti" }],
  }),
  component: ProdottiPage,
});

function ProdottiPage() {
  const productsHidden = useProductsHidden();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/prodotti/" });
  useEffect(() => {
    if (productsHidden) navigate({ to: "/", replace: true });
  }, [productsHidden, navigate]);
  if (productsHidden) return null;
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ["public-products"],
    queryFn: () => listPublicProducts(),
  });
  const PRODUCTS = data?.products ?? [];
  const FEATURED = PRODUCTS.filter((p) => p.featured);

  const toggle = (key: "cat" | "an", value: string) => {
    const current = search[key] as string[];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    navigate({ to: ".", search: (prev: any) => ({ ...prev, [key]: next }), resetScroll: false });
  };

  const filtered = useMemo(() => {
    let list = PRODUCTS.filter((p) => p.category !== "abatina");
    if (search.cat.length) list = list.filter((p) => search.cat.includes(p.category));
    if (search.an.length) list = list.filter((p) => search.an.includes(p.animal));
    if (search.q) {
      const q = search.q.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q) || p.shortDescription.toLowerCase().includes(q));
    }
    switch (search.sort) {
      case "prezzo-asc": list.sort((a, b) => effectivePrice(a) - effectivePrice(b)); break;
      case "prezzo-desc": list.sort((a, b) => effectivePrice(b) - effectivePrice(a)); break;
      case "nome": list.sort((a, b) => a.name.localeCompare(b.name)); break;
      case "novita": list.reverse(); break;
    }
    return list;
  }, [search, PRODUCTS]);

  const hasFilters = search.cat.length > 0 || search.an.length > 0 || search.q !== "";

  return (
    <>
      {/* HEADER (no hero image) */}
      <section className="bg-brand-cream pt-16 pb-12 border-b border-brand-gold/20">
        <div className="container mx-auto px-4 text-center">
          <Eyebrow>Catalogo</Eyebrow>
          <h1 className="font-display text-brand-charcoal text-4xl md:text-6xl mt-3">Il nostro bancone digitale</h1>
          <p className="text-brand-warm-gray mt-4 max-w-2xl mx-auto font-italic-serif italic text-lg">
            Carni, salumi, preparati e piatti pronti. Tutto preparato in bottega con cura artigianale.
          </p>
        </div>
      </section>

      {/* PRODOTTI IN EVIDENZA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <Reveal>
            <div className="text-center mb-3"><Eyebrow>Selezione</Eyebrow></div>
            <SectionTitle align="center">Prodotti in evidenza</SectionTitle>
            <p className="text-center font-italic-serif italic text-brand-warm-gray mt-3 text-lg">
              I best seller e le selezioni speciali del macellaio
            </p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mt-10">
            {FEATURED.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.05}>
                <ProductCard product={p} compact />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CertificationsStrip light />

      {/* CATALOGO */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-md p-3 md:p-4 shadow-[var(--shadow-soft)] sticky top-24 z-30 mb-6 text-sm">
            {/* Search + sort: sempre visibili */}
            <div className="flex flex-col md:flex-row gap-2 md:items-center md:justify-between">
              <div className="flex-1 relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-warm-gray" />
                <input
                  type="search"
                  placeholder="Cerca un prodotto..."
                  value={search.q}
                  onChange={(e) => navigate({ to: ".", search: (p: any) => ({ ...p, q: e.target.value }), resetScroll: false })}
                  className="w-full pl-8 pr-3 py-1.5 border border-brand-charcoal/15 rounded-md bg-brand-cream/40 text-xs focus:outline-none focus:border-brand-red"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setFiltersOpen((v) => !v)}
                  className="md:hidden inline-flex items-center justify-center gap-2 px-3 py-1.5 border border-brand-charcoal/15 rounded-md bg-brand-cream/40 text-xs"
                  aria-expanded={filtersOpen}
                >
                  <SlidersHorizontal size={14} />
                  Filtri
                  <ChevronDown size={14} className={cn("transition-transform", filtersOpen && "rotate-180")} />
                </button>
                <select
                  aria-label="Ordina i prodotti"
                  value={search.sort}
                  onChange={(e) => navigate({ to: ".", search: (p: any) => ({ ...p, sort: e.target.value as typeof SORTS[number] }), resetScroll: false })}
                  className="px-2 py-1.5 border border-brand-charcoal/15 rounded-md bg-brand-cream/40 text-xs flex-1 md:flex-none"
                >
                  <option value="popolari">Più venduti</option>
                  <option value="prezzo-asc">Prezzo crescente</option>
                  <option value="prezzo-desc">Prezzo decrescente</option>
                  <option value="nome">Nome A-Z</option>
                  <option value="novita">Novità</option>
                </select>
              </div>
            </div>

            {/* Filtri (collassabili su mobile, sempre aperti su md+) */}
            <div className={cn("overflow-hidden transition-all md:!max-h-none md:!opacity-100 md:mt-3", filtersOpen ? "max-h-[600px] opacity-100 mt-3" : "max-h-0 opacity-0 mt-0 md:mt-3")}>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-brand-warm-gray mb-1.5">Categoria</div>
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORIES.map((c) => {
                    const on = search.cat.includes(c);
                    return (
                      <button
                        key={c}
                        onClick={() => toggle("cat", c)}
                        className={cn(
                          "inline-flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-full text-[11px] uppercase tracking-wider border transition-colors",
                          on ? "bg-brand-red border-brand-red text-white" : "border-brand-charcoal/20 hover:border-brand-red text-brand-charcoal"
                        )}
                      >
                        <span className={cn("w-7 h-7 rounded-full flex items-center justify-center", on ? "bg-white/20" : "bg-brand-cream-dark/50")}>
                          <CategoryIcon category={c} size={18} />
                        </span>
                        {CATEGORY_LABELS[c]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-brand-warm-gray mb-1.5">Animale</div>
                <div className="flex flex-wrap gap-1.5">
                  {ANIMALS.map((a) => {
                    const on = search.an.includes(a);
                    return (
                      <button
                        key={a}
                        onClick={() => toggle("an", a)}
                        className={cn(
                          "inline-flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-full text-[11px] uppercase tracking-wider border transition-colors",
                          on ? "bg-brand-green border-brand-green text-white" : "border-brand-charcoal/20 hover:border-brand-green text-brand-charcoal"
                        )}
                      >
                        <span className={cn("w-8 h-8 rounded-full flex items-center justify-center", on ? "bg-white/20" : "bg-brand-cream-dark/50")}>
                          <AnimalIcon animal={a} size={22} />
                        </span>
                        {ANIMAL_LABELS[a]}
                      </button>
                    );
                  })}
                </div>
              </div>

              {hasFilters && (
                <div className="mt-3">
                  <button
                    onClick={() => navigate({ to: ".", search: { cat: [], an: [], q: "", sort: "popolari" }, resetScroll: false })}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-brand-red hover:underline"
                  >
                    <X size={12} /> Reset filtri
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="text-sm text-brand-warm-gray mb-4">
            {filtered.length} prodott{filtered.length === 1 ? "o" : "i"}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {filtered.map((p, i) => (
              <Reveal key={p.slug} delay={Math.min(i * 0.02, 0.3)}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 text-brand-warm-gray">
              Nessun prodotto trovato. Prova a modificare i filtri.
            </div>
          )}
        </div>
      </section>

      <ReviewsMarquee />
    </>
  );
}
